import {
  type CSSProperties,
  type RefCallback,
  useCallback,
  useRef,
  useState,
} from 'react';
import { LogoImage } from './logo-image';

export type LogosBannerLogo = {
  src: string;
  alt?: string;
};

export type LogosBannerSize = 'default' | 'large';

export type LogosBannerProps = {
  logos: LogosBannerLogo[];
  /** `large` renders the Figma "Large size" banner (bigger logo frames). */
  size?: LogosBannerSize;
  /**
   * Hex color (e.g. "#FFFFFF"): renders every logo as a flat silhouette of
   * this color (see LogoImage). Null/undefined renders the logos untouched.
   */
  fillColor?: string | null;
  className?: string;
  style?: CSSProperties;
};

type Flow = 'row' | 'column';

type Layout = {
  /** Number of columns at this breakpoint. */
  columns: number;
  /** `row` = fill left→right then down; `column` = fill top→bottom then right. */
  flow: Flow;
  /** Padding on the grid container (banner vertical padding + horizontal inset). */
  gridPadding: string;
  /** Vertical gap between grid rows. */
  rowGap: number;
  /**
   * Fixed track width in px: tracks are spread edge-to-edge (space-between).
   * Omitted = fluid equal-width tracks.
   */
  trackWidth?: number;
  /** Per-cell padding (controls logo spacing + banner height in fluid layouts). */
  cellPadding: string;
  /**
   * Logo frame box (large size); the img fills it with `object-fit: contain`.
   * Omitted = bare img capped by `logoMaxHeight`/`logoMaxWidth` (default size).
   */
  frame?: CSSProperties;
  /** Caps the bare logo to the breakpoint's frame size (default size only). */
  logoMaxHeight?: number;
  logoMaxWidth?: number;
};

/**
 * Resolves the default-size layout, mirroring the Figma "Widget Logos Banner"
 * Default size breakpoints:
 *
 * - Desktop (≥1024px): a single row of all logos (88×44 frames).
 * - Tablet (768–1023px): 4-column row-major grid (88×44 frames).
 * - Mobile (<768px): 2-column **column-major** grid (72×36 frames) — the first
 *   half of the logos fills the left column.
 */
function resolveDefaultLayout(width: number, count: number): Layout {
  if (width < 768) {
    // Mobile: px-8 py-[7.5px], max-h-9 (36px) max-w-[72px]
    return {
      columns: 2,
      flow: 'column',
      gridPadding: '0',
      rowGap: 0,
      cellPadding: '7.5px 32px',
      logoMaxHeight: 36,
      logoMaxWidth: 72,
    };
  }
  if (width < 1024) {
    // Tablet: px-5 py-[15px], max-h-11 (44px) max-w-[88px]
    return {
      columns: 4,
      flow: 'row',
      gridPadding: '0',
      rowGap: 0,
      cellPadding: '15px 20px',
      logoMaxHeight: 44,
      logoMaxWidth: 88,
    };
  }
  // Desktop: px-5 py-5, single row, max-h-11 (44px) max-w-[88px]
  return {
    columns: count,
    flow: 'row',
    gridPadding: '0',
    rowGap: 0,
    cellPadding: '20px 20px',
    logoMaxHeight: 44,
    logoMaxWidth: 88,
  };
}

/**
 * Resolves the large-size layout, mirroring the Figma "Widget Logos Banner"
 * Large size breakpoints. Unlike the default size, logos sit in fixed-size
 * frames spread edge-to-edge (space-between) on tablet/desktop:
 *
 * - Desktop (≥1280px): single row, 128×64 frames, py-5 px-5.
 * - Desktop (1024–1279px): single row, 112×56 frames, py-5 px-5.
 * - Tablet (768–1023px): 2 row-major rows of 4, 112×56 frames, py-5 px-10,
 *   32px between rows.
 * - Mobile (<768px): 2-column **column-major** grid, py-3, fluid 2:1 frames
 *   capped at 96×48 with px-8 insets, 16px between rows.
 */
function resolveLargeLayout(width: number, count: number): Layout {
  if (width < 768) {
    return {
      columns: 2,
      flow: 'column',
      gridPadding: '12px 0',
      rowGap: 16,
      cellPadding: '0 32px',
      frame: {
        width: '100%',
        maxWidth: 96,
        maxHeight: 48,
        aspectRatio: '2 / 1',
      },
    };
  }
  if (width < 1024) {
    return {
      columns: 4,
      flow: 'row',
      gridPadding: '20px 40px',
      rowGap: 32,
      trackWidth: 112,
      cellPadding: '0',
      frame: { width: 112, height: 56 },
    };
  }
  if (width < 1280) {
    return {
      columns: count,
      flow: 'row',
      gridPadding: '20px 20px',
      rowGap: 0,
      trackWidth: 112,
      cellPadding: '0',
      frame: { width: 112, height: 56 },
    };
  }
  return {
    columns: count,
    flow: 'row',
    gridPadding: '20px 20px',
    rowGap: 0,
    trackWidth: 128,
    cellPadding: '0',
    frame: { width: 128, height: 64 },
  };
}

function resolveLayout(size: LogosBannerSize, width: number, count: number): Layout {
  return size === 'large'
    ? resolveLargeLayout(width, count)
    : resolveDefaultLayout(width, count);
}

/**
 * Tracks the rendered width of an element via `ResizeObserver`. Uses the
 * element's own document/realm so it works when the banner is portaled into an
 * iframe (a parent-realm observer can be unreliable across documents). Defaults
 * to a desktop width until the first measurement lands.
 */
function useElementWidth<T extends Element>(): [RefCallback<T>, number] {
  const [width, setWidth] = useState(1280);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;

    const RO = node.ownerDocument?.defaultView?.ResizeObserver ?? ResizeObserver;
    const observer = new RO((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(w);
    });
    observer.observe(node);
    observerRef.current = observer;

    const initial = node.getBoundingClientRect().width;
    if (initial) setWidth(initial);
  }, []);

  return [ref, width];
}

/**
 * Social-proof customer-logo banner. Responsive breakpoints are chosen from the
 * banner's own measured width so it renders correctly both in the app (Tailwind
 * context) and when portaled into a style-less iframe.
 *
 * Two sizes mirror the Figma "Widget Logos Banner" variants: `default` centres
 * each logo in fluid contiguous cells; `large` uses bigger fixed frames spread
 * edge-to-edge (with a dedicated ≥1280px breakpoint). Row-major and
 * column-major fill let the same logo order flow differently per breakpoint
 * (mobile fills top→bottom).
 */
export function LogosBanner({
  logos,
  size = 'default',
  fillColor,
  className,
  style,
}: LogosBannerProps) {
  const [ref, width] = useElementWidth<HTMLDivElement>();

  const visibleLogos = logos.filter((logo) => logo.src);

  if (visibleLogos.length === 0) return null;

  const layout = resolveLayout(size, width, visibleLogos.length);
  const { columns, flow, trackWidth, cellPadding, frame } = layout;
  const rows = Math.ceil(visibleLogos.length / columns);

  const gridStyle: CSSProperties = {
    display: 'grid',
    width: '100%',
    boxSizing: 'border-box',
    padding: layout.gridPadding,
    rowGap: layout.rowGap,
    gridTemplateColumns: trackWidth
      ? `repeat(${columns}, ${trackWidth}px)`
      : `repeat(${columns}, minmax(0, 1fr))`,
    ...(trackWidth ? { justifyContent: 'space-between' } : {}),
    ...(flow === 'column'
      ? {
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
          gridAutoFlow: 'column',
        }
      : {}),
  };

  return (
    <div className={className} ref={ref} style={{...gridStyle, ...style}}>
        {visibleLogos.map((logo, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: cellPadding,
            }}
          >
            {frame ? (
              <div style={frame}>
                <LogoImage
                  src={logo.src}
                  alt={logo.alt}
                  fillColor={fillColor}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </div>
            ) : (
              <LogoImage
                src={logo.src}
                alt={logo.alt}
                fillColor={fillColor}
                style={{
                  objectFit: 'contain',
                  maxHeight: layout.logoMaxHeight,
                  maxWidth: layout.logoMaxWidth,
                }}
              />
            )}
          </div>
        ))}
      </div>
  );
}
